import {
  useCallback, useEffect, useState,
} from 'react';
import { toast } from 'react-toastify';
import DashboardService from '../../../services/affreightment/DashboardService';
import { useAppContext } from '../../../contexts/auth';
import useApiCall from '../../../hooks/useApiCall';
import { LinesType, OfferType, UserCostType, UserProfileType, VtUsageType, WarningsType } from './types';

type StartDateType = {
  month: string;
  year: number;
}

type WorkplacesType = {
  value: number;
  label: string;
}[];

export default function useDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [isWorkplacesLoading, setIsWorkplacesLoading] = useState(true);
  const [isWarningsFilterLoading, setIsWarningsFilterLoading] = useState(false);
  const [isLinesPerformanceFilterLoading, setIsLinesPerformanceFilterLoading] = useState(false);
  const [workplaces, setWorkplaces] = useState<WorkplacesType>([{}] as WorkplacesType);
  const [selectedWorkplace, setSelectedWorkplace] = useState({ value: 0, label: 'Todos' });
  const [doesWorkplacesListExists, setDoesWorkplacesListExists] = useState(false);
  const [apiWorkplacesFetched, setApiWorkplacesFetched] = useState(false);
  const [apiDashboardFetched, setApiDashboardFetched] = useState(false);

  const [startDate, setStartDate] = useState<StartDateType>({} as StartDateType);
  const [selectedStartMonth, setSelectedStartMonth] = useState<number | string>('');
  const [selectedStartYear, setSelectedStartYear] = useState<number | string>('');
  const [selectStartDateModalShow, setSelectStartDateModalShow] = useState(false);

  const [dashboardOfferData, setDashboardOfferData] = useState<OfferType>({} as OfferType);
  const [dashboardUserCostData, setDashboardUserCostData] = useState<UserCostType>({} as UserCostType);
  const [dashboardLinesData, setDashboardLinesData] = useState<LinesType>({} as LinesType);
  const [dashboardUserProfileData, setDashboardUserProfileData] = useState<UserProfileType>({} as UserProfileType);
  const [dashboardWarningsData, setDashboardWarningsData] = useState<WarningsType>({} as WarningsType);
  const [dashboardVtUsageData, setDashboardVtUsageData] = useState<VtUsageType>({} as VtUsageType);
  const [dashboardVtEvolutionData, setDashboardVtEvolutionData] = useState([]);
  const [dashboardAffreightmentEvolutionData, setDashboardAffreightmentEvolutionData] = useState([]);
  const [dashboardLinesPerformanceData, setDashboardLinesPerformanceData] = useState([]);

  const { selectedCompany, userData } = useAppContext();
  const companyCode = selectedCompany.value;
  const { userCode, userProfile } = userData;

  const { apiCall } = useApiCall();

  const getWorkplaces = useCallback(async () => {
    await apiCall({
      apiToCall: DashboardService.getWorkplaces,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsWorkplacesLoading(true),
      onEndLoad: () => setIsWorkplacesLoading(false),
      actionAfterResponse: (response) => {
        const workplacesData: { codLocTrab: number, descrLocTrab: string }[] = response.locaisTrabalho;
        const ltOptions = workplacesData?.map((lt) => (
          { value: lt.codLocTrab, label: lt.descrLocTrab }
        ));
        setWorkplaces(ltOptions);
        setDoesWorkplacesListExists(response.listaLT);
        setApiWorkplacesFetched(true);
      },
      catchAction: () => setApiWorkplacesFetched(false),
      catchMessage: 'Não foi possível recuperar os locais de trabalho',
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const getDashboardData = useCallback(async (codLocTrab: number) => {
    await apiCall({
      apiToCall: DashboardService.getDashboardData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile },
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codLocTrab,
        tipoResultado: 0,
        dataPesquisaIni: new Date().toJSON().slice(0, 10),
        mes: 0,
        ano: 0,
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        setDashboardOfferData(response?.oferta);
        setDashboardUserCostData(response?.custo);
        setDashboardLinesData(response?.linhas);
        setDashboardUserProfileData(response?.perfilUsu);
        setDashboardWarningsData(response?.advertencia);
        setDashboardVtUsageData(response?.usamVT);
        setDashboardVtEvolutionData(response?.evolucaoVT);
        setDashboardAffreightmentEvolutionData(response?.evolucaoFreta);
        setDashboardLinesPerformanceData(response?.linhasPerform);
        setApiDashboardFetched(true);
      },
      catchAction: () => setApiDashboardFetched(false),
      catchMessage: 'Não foi possível carregar o dashboard. Por favor, tente novamente',
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const filterWarningsByMonth = useCallback(async ({
    locTrab,
    month,
    year,
  } : { locTrab: number, month: string, year: number }) => {
    apiCall({
      apiToCall: DashboardService.getDashboardData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile },
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codLocTrab: locTrab,
        tipoResultado: 2,
        dataPesquisaIni: new Date().toJSON().slice(0, 10),
        mes: month,
        ano: year,
      }),
      onStartLoad: () => setIsWarningsFilterLoading(true),
      onEndLoad: () => setIsWarningsFilterLoading(false),
      actionAfterResponse: (response) => {
        setDashboardWarningsData(response?.advertencia);
        setApiDashboardFetched(true);
      },
      catchAction: () => setApiDashboardFetched(false),
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const filterLinesPerformanceByDate = useCallback(async ({
    locTrab,
    date,
  }: { locTrab: number, date: string }) => {
    await apiCall({
      apiToCall: DashboardService.getDashboardData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile },
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codLocTrab: locTrab,
        tipoResultado: 1,
        dataPesquisaIni: date,
        mes: 0,
        ano: 0,
      }),
      onStartLoad: () => setIsLinesPerformanceFilterLoading(true),
      onEndLoad: () => setIsLinesPerformanceFilterLoading(false),
      actionAfterResponse: (response) => {
        setDashboardLinesPerformanceData(response?.linhasPerform);
        setApiDashboardFetched(true);
      },
      catchAction: () => setApiDashboardFetched(false),
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const getStartDate = useCallback(async () => {
    await apiCall({
      apiToCall: DashboardService.getStartDate,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        setStartDate({
          month: response.mesInicioServ,
          year: response.anoInicioServ,
        });
      },
      catchAction: () => {
        if (companyCode) {
          toast.error('Não foi possível recuperar a data de início do serviço');
        }
      }
    });
  }, [apiCall, companyCode, userCode, userProfile]);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      getWorkplaces(),
      getStartDate(),
      getDashboardData(selectedWorkplace?.value || 0)
    ]);
    setIsLoading(false);
  }, [getWorkplaces, getStartDate, getDashboardData, selectedWorkplace?.value]);

  const chooseStartDate = useCallback(async () => {
    await apiCall({
      apiToCall: DashboardService.chooseStartDate,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        servico: 16,
        mes: selectedStartMonth,
        ano: selectedStartYear,
      }),
      onStartLoad: () => {
        setSelectStartDateModalShow(false);
        setIsLoading(true);
      },
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (response.codigo) {
          loadDashboard();
          toast.success('Data de início definida com sucesso');
          return;
        }
        toast.error(`Não foi possível configurar a data de início (${response.msg})`);
      },
      catchMessage: 'Não foi possível configurar a data de início',
    });
  }, [apiCall, userCode, userProfile, companyCode, selectedStartMonth, selectedStartYear, loadDashboard]);

  const handleWorkplaceChange = ({ value, label }: { value: number, label: string }) => {
    setSelectedWorkplace({ value, label });
    getDashboardData(value);
  };

  useEffect(() => {
    if (companyCode) {
      loadDashboard();
    }
  }, [companyCode, loadDashboard]);

  function handleTryGetWorkplacesAgain() {
    getWorkplaces();
  }

  return {
    userProfile,
    companyCode,
    doesWorkplacesListExists,
    apiWorkplacesFetched,
    isLoading,
    selectedWorkplace,
    apiDashboardFetched,
    dashboardOfferData,
    dashboardUserCostData,
    dashboardLinesData,
    dashboardWarningsData,
    filterWarningsByMonth,
    dashboardVtUsageData,
    dashboardUserProfileData,
    dashboardAffreightmentEvolutionData,
    dashboardVtEvolutionData,
    dashboardLinesPerformanceData,
    filterLinesPerformanceByDate,
    isWarningsFilterLoading,
    isLinesPerformanceFilterLoading,
    workplaces,
    handleWorkplaceChange,
    handleTryGetWorkplacesAgain,
    isWorkplacesLoading,
    startDate,
    selectStartDateModalShow,
    setSelectedStartYear,
    setSelectedStartMonth,
    selectedStartYear,
    selectedStartMonth,
    chooseStartDate,
    setSelectStartDateModalShow,
  };
}
